import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

const SUJETS = ["Stage de création", "Création individuelle", "Création sur un lieu ancien", "Commande tambour", "Soin énergétique", "Harmonisation de lieu", "Consultation holistique", "Cercle & événement", "Autre question"];

export const ContactForm = ({ sujetPreset, messagePreset = "", titre = "Écrivez-nous", testidPrefix = "contact" }) => {
  const { t } = useLocale();
  const [form, setForm] = useState({ nom: "", email: "", sujet: sujetPreset || "Autre question", message: messagePreset });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.contact(form);
      toast.success(res.message);
      setSent(true);
    } catch {
      toast.error(t("Le message n'a pas pu partir — réessayez ou écrivez-nous directement."));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-sage-light/50 border border-sage/30 rounded-2xl p-10 text-center" data-testid={`${testidPrefix}-success`}>
        <CheckCircle2 className="w-10 h-10 text-sage mx-auto mb-4" />
        <p className="font-serif text-2xl text-bark mb-2">{t("Votre message voyage vers nous")}</p>
        <p className="text-sm text-fumee">{t("Nous vous répondons avec soin sous 48 h ouvrées.")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`${testidPrefix}-form`}>
      <p className="font-serif text-2xl text-bark">{t(titre)}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor={`${testidPrefix}-nom`} className="eyebrow block mb-2">{t("Nom")}</label>
          <input id={`${testidPrefix}-nom`} data-testid={`${testidPrefix}-nom`} required value={form.nom} onChange={set("nom")}
            className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-email`} className="eyebrow block mb-2">{t("Courriel")}</label>
          <input id={`${testidPrefix}-email`} data-testid={`${testidPrefix}-email`} type="email" required value={form.email} onChange={set("email")}
            className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
        </div>
      </div>
      <div>
        <label htmlFor={`${testidPrefix}-sujet`} className="eyebrow block mb-2">{t("Sujet")}</label>
        <select id={`${testidPrefix}-sujet`} data-testid={`${testidPrefix}-sujet`} value={form.sujet} onChange={set("sujet")}
          className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra">
          {SUJETS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor={`${testidPrefix}-message`} className="eyebrow block mb-2">{t("Votre message")}</label>
        <textarea id={`${testidPrefix}-message`} data-testid={`${testidPrefix}-message`} required rows={5} value={form.message} onChange={set("message")}
          className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra resize-none" />
      </div>
      <button type="submit" data-testid={`${testidPrefix}-submit`} disabled={loading}
        className="px-8 py-3.5 rounded-full bg-earth text-sand text-sm tracking-wide hover:bg-terra hover:text-earth transition-colors duration-300 disabled:opacity-60">
        {loading ? t("Envoi…") : t("Envoyer le message")}
      </button>
    </form>
  );
};
